/**
 * The Table mode's inline saves (Phase C2), through the SAME endpoints and permissions as the
 * product drawer:
 *   - price → PATCH /inventory/:id/price with the typed amount (exact in the store's currency);
 *     the cell shows what was typed, marked "saving", until the server's listed price replaces it;
 *     a refusal leaves the server's price showing and the reason in the cell;
 *   - quantity → PATCH /inventory/:id/quantity as a DELTA with an Idempotency-Key (a retried
 *     request can never apply it twice); the cached row moves at once and is restored on refusal.
 *
 * Status is kept per product, and a row only re-renders when its own status object changes.
 */

import { useCallback, useState } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { api, createIdempotencyKey, withIdempotencyKey } from '../../../utils/api'
import type { Currency } from '../../../contexts/CurrencyContext'
import { restoreInventoryListQueries, snapshotInventoryListQueries } from './optimisticInventoryCache'
import type { InventoryProduct } from './inventoryTypes'
import {
  apiErrorText,
  manualPricePayload,
  parseMoneyInput,
  parseQuantityInput,
  patchCachedProduct,
  withQuantityDelta,
  withSavedPrice,
  type CellStatus,
  type EditableField,
} from './inventoryTableEdit'

export interface InventoryTableEdits {
  status: ReadonlyMap<string, CellStatus>
  /** Validate and send. Resolves false (editor stays open) when the text itself is wrong. */
  save: (product: InventoryProduct, field: EditableField, text: string) => Promise<boolean>
  clear: (productId: string) => void
}

export function useInventoryTableEdits(params: {
  queryClient: QueryClient
  currency: Currency
  baseCurrency: Currency
  tryConvertPrice: (cents: number, from: Currency, to: Currency) => number | null
  formatPrice: (cents: number, fromCurrency?: Currency) => string
}): InventoryTableEdits {
  const { queryClient, currency, baseCurrency, tryConvertPrice, formatPrice } = params
  const [status, setStatus] = useState<ReadonlyMap<string, CellStatus>>(() => new Map())

  const setFor = useCallback((productId: string, next: CellStatus | null) => {
    setStatus((current) => {
      const copy = new Map(current)
      if (next) copy.set(productId, next)
      else copy.delete(productId)
      return copy
    })
  }, [])

  const clear = useCallback((productId: string) => setFor(productId, null), [setFor])

  const save = useCallback(async (product: InventoryProduct, field: EditableField, text: string): Promise<boolean> => {
    if (field === 'price') {
      const cents = parseMoneyInput(text)
      if (cents === null) {
        setFor(product.id, { field, state: 'error', message: 'Enter a price like 12.50' })
        return false
      }
      const built = manualPricePayload(cents, currency, baseCurrency, tryConvertPrice)
      if (!built.ok) {
        setFor(product.id, { field, state: 'error', message: built.message })
        return false
      }
      setFor(product.id, { field, state: 'saving', text: formatPrice(cents, currency) })
      try {
        const response = await api.patch(`/inventory/${product.id}/price`, built.payload)
        const saved = response.data?.data?.product ?? response.data?.product
        if (saved) patchCachedProduct(queryClient, product.id, (row) => withSavedPrice(row, saved))
        setFor(product.id, null)
        // The other figures (manual vs market, margin) come with the next list read.
        void queryClient.invalidateQueries({ queryKey: ['inventory', 'list'] })
      } catch (error) {
        setFor(product.id, { field, state: 'error', message: apiErrorText(error, 'The price could not be saved.') })
      }
      return true
    }

    const next = parseQuantityInput(text)
    if (next === null) {
      setFor(product.id, { field, state: 'error', message: 'Enter a whole number of units' })
      return false
    }
    const delta = next - product.quantity
    if (delta === 0) {
      setFor(product.id, null)
      return true
    }
    if (next < product.reservedQuantity) {
      setFor(product.id, { field, state: 'error', message: `${product.reservedQuantity} reserved by open orders: stock can't go below that` })
      return false
    }
    const snapshot = snapshotInventoryListQueries(queryClient)
    patchCachedProduct(queryClient, product.id, (row) => withQuantityDelta(row, delta))
    setFor(product.id, { field, state: 'saving', text: String(next) })
    try {
      const response = await api.patch(
        `/inventory/${product.id}/quantity`,
        { quantityChange: delta, reason: 'Inventory table edit' },
        withIdempotencyKey(createIdempotencyKey()),
      )
      const saved = response.data?.data?.product ?? response.data?.product
      if (saved && typeof saved.quantity === 'number') {
        patchCachedProduct(queryClient, product.id, (row) => ({
          ...row,
          quantity: saved.quantity,
          availableQuantity: saved.availableQuantity ?? row.availableQuantity,
          reservedQuantity: saved.reservedQuantity ?? row.reservedQuantity,
        }))
      }
      setFor(product.id, null)
      void queryClient.invalidateQueries({ queryKey: ['inventory', 'stats'] })
    } catch (error) {
      restoreInventoryListQueries(queryClient, snapshot)
      setFor(product.id, { field, state: 'error', message: apiErrorText(error, 'The stock could not be saved.') })
    }
    return true
  }, [queryClient, currency, baseCurrency, tryConvertPrice, formatPrice, setFor])

  return { status, save, clear }
}
